import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import TextField from '../components/TextField';
import Button from '../components/Button';

function Contact() {
	const [form, setForm] = useState({
		full_name: '',
		email: '',
		phone: '',
		company_name: '',
		service: '',
		message: '',
		agree_to_sms: false,
		agree_to_tcpa: false
	});
	const [submitting, setSubmitting] = useState(false);
	const [status, setStatus] = useState(null);
	const [websiteTrap, setWebsiteTrap] = useState('');

	const onChange = (e) => {
		const { name, value, type, checked } = e.target;
		setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitting(true);
		setStatus(null);
		try {
			// Honeypot trap: if filled, treat as success but do not submit
			if (websiteTrap) {
				setStatus({ ok: true, msg: 'Thank you! We will be in touch shortly.' });
				setSubmitting(false);
				setForm({
					full_name: '', email: '', phone: '', company_name: '', service: '', message: '',
					agree_to_sms: false, agree_to_tcpa: false
				});
				return;
			}
			// Insert lead
			const { data: lead, error: leadError } = await supabase
				.from('apex_specialty_leads')
				.insert([{
					full_name: form.full_name,
					email: form.email,
					phone: form.phone,
					company_name: form.company_name,
					financing_amount: null,
					equipment_type: form.service || null,
					message: form.message || null,
					agree_to_sms: form.agree_to_sms,
					agree_to_tcpa: form.agree_to_tcpa,
					source: 'apex_specialty'
				}])
				.select()
				.single();
			if (leadError) throw leadError;

			// Insert SMS consent audit if consented
			if (form.agree_to_sms) {
				const consentText =
					'I consent to receive marketing SMS from APEX SPECIALTY CONTRACTING LLC (Appex Connect Hub). ' +
					'Msg/data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help. ' +
					'Consent not required to obtain credit.';
				await supabase.from('sms_consents').insert([{
					lead_id: lead?.id ?? null,
					phone: form.phone,
					email: form.email || null,
					name: form.full_name || null,
					consent_text: consentText,
					consent_method: 'web_form_checkbox',
					referral_url: window.location.href,
					user_agent: navigator.userAgent,
					ip_address: null, // backend or edge function can enrich IP if needed
					brand: 'APEX SPECIALTY CONTRACTING LLC',
					website: 'appexconnecthub.com'
				}]);
			}

			setStatus({ ok: true, msg: 'Thank you! We will be in touch shortly.' });
			setForm({
				full_name: '', email: '', phone: '', company_name: '', service: '', message: '',
				agree_to_sms: false, agree_to_tcpa: false
			});
		} catch (err) {
			console.error(err);
			setStatus({ ok: false, msg: 'Something went wrong. Please try again.' });
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<section className="section">
			<div className="container">
				<Helmet>
					<title>Contact • Appex Connect Hub</title>
					<meta name="description" content="Request information from Appex Connect Hub. Not a lender; we connect opt-in consumers to financial service providers with transparent SMS consent." />
				</Helmet>
				<h2>Request Information</h2>
				<div className="card" style={{ padding: 18, marginBottom: 14 }}>
					<p className="helper">
						Appex Connect Hub is operated by APEX SPECIALTY CONTRACTING LLC. We are not a lender and do not
						make credit decisions. By submitting this form, you authorize us to share your information with
						our network of financial service providers.
					</p>
					<p className="helper">
						Need help fast? Email <a href="mailto:support@appexconnecthub.com">support@appexconnecthub.com</a> or call your assigned project coordinator if provided.
					</p>
					{!isSupabaseConfigured && (
						<p className="helper" style={{ color: 'crimson', marginTop: 8 }}>
							Note: Form submissions are temporarily disabled because data storage is not configured.
						</p>
					)}
				</div>
				<form className="form form-grid" style={{ marginTop: 16, maxWidth: 820 }} onSubmit={handleSubmit} noValidate>
					{/* Honeypot field */}
					<input
						type="text"
						name="website"
						value={websiteTrap}
						onChange={(e) => setWebsiteTrap(e.target.value)}
						style={{ display: 'none' }}
						tabIndex={-1}
						autoComplete="off"
						aria-hidden="true"
					/>
					<TextField id="full_name" label="Full Name *" name="full_name" required value={form.full_name} onChange={onChange} />
					<TextField id="email" label="Email *" type="email" name="email" required value={form.email} onChange={onChange} placeholder="you@example.com" />
					<TextField id="phone" label="Mobile Phone *" name="phone" required value={form.phone} onChange={onChange} placeholder="(555) 555-5555" helper="Provide a mobile number for SMS communications (optional but recommended)." pattern="^[0-9+()\\-\\s]{7,}$" />
					<TextField id="company_name" label="Company" name="company_name" value={form.company_name} onChange={onChange} />
					<div className="field">
						<label htmlFor="service">Service Interest</label>
						<select id="service" name="service" value={form.service} onChange={onChange}>
							<option value="">Select</option>
							<option value="equipment_financing">Equipment Financing</option>
							<option value="personal_loan_referral">Personal Loan Referral</option>
							<option value="auto_loan_referral">Auto Loan Referral</option>
							<option value="mortgage_referral">Mortgage Referral</option>
							<option value="other">Other</option>
						</select>
					</div>
					<div className="field" style={{ gridColumn: '1 / -1' }}>
						<label htmlFor="message">Additional Details</label>
						<textarea id="message" name="message" rows={4} value={form.message} onChange={onChange} />
					</div>

					<div className="consentBox" style={{ gridColumn: '1 / -1' }}>
						<p><strong>Required Consents</strong></p>
						<div className="consent-grid">
							<div className="consent-row">
								<input
									type="checkbox"
									id="agree_to_sms"
									name="agree_to_sms"
									checked={form.agree_to_sms}
									onChange={onChange}
									style={{ marginTop: 4 }}
								/>
								<label htmlFor="agree_to_sms" className="helper">
									I consent to receive marketing text messages from APEX SPECIALTY CONTRACTING LLC (Appex Connect Hub) and its service partners.
									Message & data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help. Consent not required to obtain credit.
								</label>
							</div>
							<div className="consent-row">
								<input
									type="checkbox"
									id="agree_to_tcpa"
									name="agree_to_tcpa"
									required
									checked={form.agree_to_tcpa}
									onChange={onChange}
									style={{ marginTop: 4 }}
								/>
								<label htmlFor="agree_to_tcpa" className="helper">
									I agree to be contacted via phone, email, and SMS at the number and address provided, which may include autodialed or prerecorded messages,
									even if my number is on a Do Not Call list.
								</label>
							</div>
						</div>
						<p className="helper" style={{ marginTop: 8 }}>
							By submitting, I agree to the Privacy Policy and Terms.
						</p>
					</div>

					<div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-start' }}>
						<Button type="submit" disabled={submitting || !isSupabaseConfigured}>
							{submitting ? 'Submitting…' : 'Submit Request'}
						</Button>
					</div>
					{status && (
						<p className="helper" style={{ color: status.ok ? 'green' : 'crimson' }}>{status.msg}</p>
					)}
				</form>
			</div>
		</section>
	);
}

export default Contact;


